import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { Policy } from '../customer/Policy';
import { Utils } from '../Utils';
import requestPromise from 'request-promise-native';

import { PolicyAdminStatus } from './PolicyAdminStatus';
import { BeneficiaryInfoResponse } from './interface/BeneficiaryInfoResponse';
import { GglQuery } from './graphQlQuery/gqlQuery';
import { InitialInvoice } from './interface/InitialInvoiceInfoResponse';
import { PremiumRequest, ProductServiceClient } from '../productService/ProductServiceClient';
import safeJsonStringify from 'safe-json-stringify';
import moment from 'moment';
import { Product } from '../static/Product';
import { serverOnly } from '../helpers';
import { TPolicyAdminPartyInfo } from '../interfaces/CommonTypes';
import { Address, SphinxReturnTypes } from '../interfaces/CommonTypes';
import { ProductApiTypes } from '@hit/policy-admin-types';
import * as uuid from 'uuid';
import { BillingInfoDTO, CombinedSegmentDTO, BasicPolicyInfoDTO, PolicyValuesDTO } from './PolicyAdminRiders';
import { ProductInfoPayload } from '@hit/policy-admin-types/dist/data-model/Types';
import { DateDMY } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { LoggerSingleton, Category, PiiLevelEnum } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

@supertypeClass({ toClient: false })
export class PolicyAdminClient {
  static readonly END_POINT_PATHS: { [key: string]: string } = {
    GET_POLICY: 'policy-admin/get-policy',
    GET_POLICY_ID: 'policy-admin/get-policy-id',
    GET_POLICY_INFO: 'policy-admin/get-policy-info',
    GET_POLICY_PACKET: 'policy-admin/get-policy-packet',
    GET_ANNUITY_PACKET: 'policy-admin/get-policy-docs',
    GET_ANNUAL_STATEMENT: 'policy-admin/get-policy-docs',
    GET_PARTIAL_CONVERSION_QUOTE: 'policy-admin/partial-convert-policy',
    GET_POLICY_DETAILS: 'policy-admin/get-policy',
    GET_POLICY_PREMIUM_BREAKDOWN: 'policy-admin/get-premium-breakdown',
    GET_POLICY_INFO_BATCH: 'policy-admin/get-policy-info-batch',
    CAN_CREATE_TRANSACTION_WORKFLOW: 'policy-admin/can-create-transaction-workflow',
    CASH_VALUE: 'policy-admin/get-guaranteed-cash-value-quote-batch',
    UPDATE_OWNER_EMAIL: 'policy-admin/update-owner-email',
    GET_OWNERS: 'policy-admin/get-owners',
    UPDATE_OWNER_ADDRESS: 'sphinx/coverpath/owner-address',
    UPDATE_PAYMENT_FREQUENCY: 'sphinx/coverpath/payment-frequency',
    GET_INSURANCE_SEGMENT_INFORMATION: 'policy-admin/get-insurance-segment-information',
    GET_BILLING_INFO: 'policy-admin/get-billing-info',
    GET_BASIC_POLICY_INFO: 'policy-admin/get-basic-policy-info',
    GET_POLICY_VALUES: 'policy-admin/get-policy-values',
    GET_PRODUCT_INFO: 'policy-admin/get-product-info',
    GET_PAYER_PAYMENT_INFORMATION: 'policy-admin/get-payer-payment-information',
    GET_INVOICES_INFORMATION: 'policy-admin/get-invoices',
  };

  static readonly SPHINX_END_POINT_PATHS: { [key: string]: string } = {
    UPDATE_OWNER_ADDRESS: 'owner-address',
    UPDATE_PAYMENT_FREQUENCY: 'payment-frequency',
  };

  static readonly CONFIG_POLICY_ADMIN_URL: string = 'policyAdminUrl';
  static readonly CONFIG_POLICY_ADMIN_GQL_PATH: string = 'policyAdminGqlPath';
  static readonly CONFIG_POLICY_ADMIN_HEALTHCHECK_PATH: string = 'policyAdminHealthcheckPath';
  static readonly CONFIG_PA_API_CLIENT_PATH: string = 'policyAdminApiClientPath';
  static readonly CONFIG_POLICY_ADMIN_JWT_TOKEN: string = 'cpApiGatewayJWTToken';
  static readonly CONFIG_SPHINX_URL: string = 'sphinxUrl';

  static readonly REQUEST_TYPES: { [key: string]: string } = {
    POST: 'POST',
    GET: 'GET',
    PUT: 'PUT',
  };

  // Timeout in milliseconds -> minutes -> number of minutes
  static readonly DEFAULT_TIMEOUT: number = 1000 * 60 * 2;

  // ---------------------------------------
  // Service Calls

  public static async getPolicyAdminPolicy(policy: Policy): Promise<PolicyAdminPolicy> {
    const policyAdminPolicy = await PolicyAdminClient.policyAdminApiGet(policy);
    return policyAdminPolicy;
  }

  public static async getInsuranceSegmentInfo(policy: Policy): Promise<CombinedSegmentDTO> {
    const policyInfo = await PolicyAdminClient.insuranceSegmentInfoApiGet(policy);
    return policyInfo;
  }

  public static async getBillingInfo(policy: Policy): Promise<BillingInfoDTO> {
    const billingInfo = await PolicyAdminClient.billingInfoApiGet(policy);
    return billingInfo;
  }

  public static async getBasicPolicyInfo(policy: Policy): Promise<BasicPolicyInfoDTO> {
    const policyValues = await PolicyAdminClient.basicPolicyInfoApiGet(policy);
    return policyValues;
  }

  public static async getPolicyValues(policy: Policy): Promise<PolicyValuesDTO> {
    const policyValues = await PolicyAdminClient.policyValuesApiGet(policy);
    return policyValues;
  }

  public static async getProductInfo(policy: Policy): Promise<ProductInfoPayload> {
    const policyValues = await PolicyAdminClient.productInfoApiGet(policy);
    return policyValues;
  }

  @serverOnly()
  static async getPayerInfo(policy: Policy) {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_PAYER_PAYMENT_INFORMATION
    );

    try {
      return await requestPromise(options);
    } catch (error) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'PolicyAdminClient',
          activity: 'getPayerInfo',
          data: {
            policyNumber: policy.policyNumber,
            errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
          },
        },
        'Unable to find Policy Admin payer info'
      );
      return PolicyAdminClient.handlePolicyAdminStatusError(error);
    }
  }

  @serverOnly()
  static async getInvoicesInfo(policy: Policy) {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_INVOICES_INFORMATION
    );

    try {
      return await requestPromise(options);
    } catch (error) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'PolicyAdminClient',
          activity: 'getPAInvoicesInfo',
          data: {
            policyNumber: policy.policyNumber,
            errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
          },
        },
        'Unable to find Policy Admin invoices info'
      );
      return PolicyAdminClient.handlePolicyAdminStatusError(error);
    }
  }

  @serverOnly()
  public static async getPolicyAdminPolicyBatch(
    policyNumbers: Array<string>,
    channel: string
  ): Promise<Array<PolicyAdminPolicy> | undefined> {
    const policyAdminPolicies = await PolicyAdminClient.policyAdminApiBatchGet(policyNumbers, channel);
    return policyAdminPolicies;
  }

  public static async getAnnualStatement(policy: Policy): Promise<PolicyAdminPacket> {
    const annualStatementPacket = await PolicyAdminClient.annualStatementApiGet(policy);

    return annualStatementPacket;
  }

  @serverOnly()
  private static async insuranceSegmentInfoApiGet(policy: Policy): Promise<CombinedSegmentDTO> {
    const options = PolicyAdminClient.generateBatchRequestOptions(
      [policy.policyNumber],
      policy.getChannelCode(),
      PolicyAdminClient.END_POINT_PATHS.GET_INSURANCE_SEGMENT_INFORMATION
    );

    // generateBatchRequestOptions does not have the properties below, which is required for this API call.
    options['body']['context']['referenceId'] = policy.policyNumber;
    options['body']['context']['referenceType'] = 'policyNumber';
    options['body']['context']['eventName'] = PolicyAdminClient.END_POINT_PATHS.GET_INSURANCE_SEGMENT_INFORMATION;

    return requestPromise(options)
      .then((response) => {
        // If policy number does not exist in PA, following is returned:
        /*
        {
          "policyNumber": "1118406694",
            "message": "Policy does not exist in PA for policy number: 1118406694"
        } */
        if (!response || !response.policies || response.policies.length !== 1 || response.policies[0].message) {
          return undefined;
        }

        const policyAdminResponse = {
          payload: response.policies[0],
          responseStatus: PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK,
          isPolicyAdminPolicy: true,
        };

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'insuranceSegmentInfoApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: error.message,
            },
          },
          `Unable to find Policy Admin policy ${policy.policyNumber}`
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return undefined;
      });
  }

  @serverOnly()
  private static async billingInfoApiGet(policy: Policy): Promise<BillingInfoDTO> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_BILLING_INFO
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'billingInfoApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: error.message,
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async basicPolicyInfoApiGet(policy: Policy): Promise<BasicPolicyInfoDTO> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_BASIC_POLICY_INFO
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'basicPolicyInfoApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: error.message,
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async policyValuesApiGet(policy: Policy): Promise<PolicyValuesDTO> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_POLICY_VALUES
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'policyValuesApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: error.message,
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async productInfoApiGet(policy: Policy): Promise<ProductInfoPayload> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_PRODUCT_INFO
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response && response.payload;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'productInfoApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: error.message,
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async annualStatementApiGet(policy: Policy): Promise<PolicyAdminPacket> {
    const options = PolicyAdminClient.generateAnnualStatementRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_ANNUAL_STATEMENT
    );

    return requestPromise(options)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'annualStatementApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Annual Statement'
        );
        return null;
      });
  }

  public static async getPolicyPacket(policy: Policy): Promise<PolicyAdminPacket> {
    if (policy.productType === Product.productTypes.Annuity.code) {
      return PolicyAdminClient.annuityPacketApiGet(policy);
    }
    return PolicyAdminClient.policyPacketApiGet(policy);
  }

  public static async getPolicyDetails(policy: Policy) {
    return PolicyAdminClient.policyDetailsApiGet(policy);
  }

  public static async getPartialConversionQuote(
    requestOptions: PolicyAdminRequestPartialConversionQuotePayload,
    channel: string
  ) {
    try {
      const quoteResponse = await PolicyAdminClient.partialConversionQuoteApiGet(requestOptions, channel);
      return quoteResponse && quoteResponse.payload;
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'PolicyAdminClient',
          function: 'policyDetailsApiGet',
          category: UICategory.request,
          error: {
            isHumanRelated: false,
            data: {
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
              policyNumber: requestOptions.policyNumber,
              conversionAmount: requestOptions.conversionAmount,
              effectiveDate: requestOptions.effectiveDate,
            },
          },
        },
        'Unable to fetch partial conversion quote'
      );

      throw error;
    }
  }

  public static async getPolicyPremiumBreakdown(policy: Policy): Promise<PolicyPremiumBreakdown> {
    const policyAdminPolicy = await PolicyAdminClient.policyPremiumBreakdownApiGet(policy);
    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'policyAdminPremiumBreakdown',
        category: UICategory.request,
        data: {
          policyNumber: policy.policyNumber,
          tlicEffectiveDate: policy.tLICEffectiveDate,
          effectiveDate: policy.effectiveDate,
          premium: policyAdminPolicy ? policyAdminPolicy.premium : null,
        },
      },
      'Acquired policyAdminPremiumBreakdown'
    );
    return policyAdminPolicy;
  }

  public static assemblePolicyAdminUrl(channel: string, path: string): string {
    const appName = Utils.getAppName(channel);
    const policyAdminUrl = amorphicStatic.config.configStore[appName].get(PolicyAdminClient.CONFIG_POLICY_ADMIN_URL);
    return policyAdminUrl + '/' + path;
  }

  public static assembleSphinxUrl(channel: string, path: string): string {
    const appName = Utils.getAppName(channel);
    const sphinxUrl = amorphicStatic.config.configStore[appName].get(PolicyAdminClient.CONFIG_SPHINX_URL);
    return sphinxUrl + '/' + path;
  }

  public static getPolicyAdminApiHeader() {
    const cpApiGatewayJWTToken = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN);
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + cpApiGatewayJWTToken,
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
  }

  private static getPaGqlUrl(): string {
    const domain = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_URL);
    const path = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_GQL_PATH);
    return `${domain}${path}`;
  }

  public static getPaClientUrl(): string {
    const domain = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_URL);
    const path = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_PA_API_CLIENT_PATH);
    return `${domain}${path}`;
  }

  public static getSphinxPolicyAdminApiHeader(principalUserProducerId: string, principalUserName: string) {
    const header = PolicyAdminClient.getPolicyAdminApiHeader();
    const xContext = {
      requestId: Utils.generateUuid(),
      userId: principalUserProducerId,
      userName: principalUserName,
      requestSystem: 'coverpath',
    };
    header['X-Context'] = JSON.stringify(xContext);
    return header;
  }

  public static getGlobalHeadersForContext() {
    const globalHeaders = LoggerSingleton.getInstance()?.getContextFromLocalStorage?.();

    return {
      globalId: globalHeaders?.globalId,
      globalSource: globalHeaders?.globalSource,
    };
  }

  @serverOnly()
  static getContactInfo(policy: Policy): Promise<ContactInfoResponse> {
    const requestOptions = {
      method: 'POST',
      uri: PolicyAdminClient.getPaGqlUrl(),
      body: PolicyAdminClient.buildContactInfoRequest(policy.policyNumber, policy.isOwnerSameAsInsured()),
      json: true,
      auth: {
        bearer: amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN),
      },
    };

    return requestPromise(requestOptions).then((response) => PolicyAdminClient.buildContactInfoResponse(response));
  }

  @serverOnly()
  static getPaymentInfo(policy: Policy): Promise<PaymentInfoResponse> {
    const requestOptions = {
      method: 'POST',
      uri: PolicyAdminClient.getPaGqlUrl(),
      body: PolicyAdminClient.buildPaymentInfoRequest(policy.policyNumber),
      json: true,
      auth: {
        bearer: amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN),
      },
    };

    return requestPromise(requestOptions).then((response) => PolicyAdminClient.buildPaymentInfoResponse(response));
  }

  @serverOnly()
  static getBeneficiaryInfo(policy: Policy): Promise<BeneficiaryInfoResponse> {
    const channelCode = policy.getChannelCode();
    const requestOptions = {
      method: 'POST',
      uri: PolicyAdminClient.getPaGqlUrl(),
      body: PolicyAdminClient.buildBeneficiaryInfoRequest(policy.policyNumber, channelCode),
      json: true,
      auth: {
        bearer: amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN),
      },
    };

    return requestPromise(requestOptions).then((response) => PolicyAdminClient.buildBeneficiaryInfoResponse(response));
  }

  @serverOnly()
  static getInitialInvoiceInfo(
    policy: Policy,
    request?: PremiumRequest,
    productPremiumVersion?: string
  ): Promise<InitialInvoice> {
    const requestOptions = {
      method: 'POST',
      uri: PolicyAdminClient.getPaGqlUrl(),
      body: PolicyAdminClient.buildInitialInvoiceInfoRequest(policy, request, productPremiumVersion),
      json: true,
      auth: {
        bearer: amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN),
      },
    };

    return requestPromise(requestOptions).then((response) =>
      PolicyAdminClient.buildInitialInvoiceInfoResponse(response)
    );
  }

  @serverOnly()
  static getPolicyId(policy: Policy) {
    const options = PolicyAdminClient.generateRequestOptions(policy, PolicyAdminClient.END_POINT_PATHS.GET_POLICY_ID);

    return requestPromise(options)
      .then((response) => {
        return response && response.payload;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'getPolicyId',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        return PolicyAdminClient.handlePolicyAdminStatusError(error);
      });
  }

  @serverOnly()
  static getPolicy(policy: Policy) {
    const options = PolicyAdminClient.generateRequestOptions(policy, PolicyAdminClient.END_POINT_PATHS.GET_POLICY);

    return requestPromise(options)
      .then((response) => {
        return response && response.payload;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'getPolicy',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        return PolicyAdminClient.handlePolicyAdminStatusError(error);
      });
  }

  @serverOnly()
  static checkCanCreateTransactions(
    policy: Policy,
    policyAdminPolicyId: string,
    workflowNames: string[]
  ): Promise<CanCreateTransactionsResponse> {
    const requestOptions = PolicyAdminClient.buildCanCreateTransactionsRequest(
      policy,
      policyAdminPolicyId,
      workflowNames
    );

    return requestPromise(requestOptions)
      .then((response) => {
        return response && response.payload;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'checkCanCreateTransactions',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }
  // ---------------------------------------
  // Private helpers

  @serverOnly()
  public static isHealthy(channel: string): Promise<string> {
    const options = PolicyAdminClient.generateHealthcheckRequestOptions(channel);

    return requestPromise(options)
      .then((healthcheck) => healthcheck.logMsg)
      .catch((err) => err.message);
  }

  @serverOnly()
  private static policyDetailsApiGet(policy: Policy) {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_POLICY_DETAILS
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response && response.payload;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'policyDetailsApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        return PolicyAdminClient.handlePolicyAdminStatusError(error);
      });
  }

  @serverOnly()
  private static partialConversionQuoteApiGet(
    requestOptions: PolicyAdminRequestPartialConversionQuotePayload,
    channel: string
  ) {
    const url = PolicyAdminClient.assemblePolicyAdminUrl(
      channel,
      PolicyAdminClient.END_POINT_PATHS.GET_PARTIAL_CONVERSION_QUOTE
    );

    const request = {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          referenceId: requestOptions.policyNumber,
          referenceType: 'policyNumber',
          eventName: url,
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: requestOptions,
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };

    return requestPromise(request);
  }

  @serverOnly()
  private static async policyAdminApiGet(policy: Policy): Promise<PolicyAdminPolicy> {
    const options = PolicyAdminClient.generateRequestOptions(policy, PolicyAdminClient.END_POINT_PATHS.GET_POLICY_INFO);

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response && response.payload;

        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'policyAdminApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async policyPremiumBreakdownApiGet(policy: Policy): Promise<PolicyPremiumBreakdown> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_POLICY_PREMIUM_BREAKDOWN
    );

    return requestPromise(options)
      .then((response) => {
        const policyAdminResponse = response && response.payload && response.payload.policies[0];
        policyAdminResponse.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.OK;
        policyAdminResponse.isPolicyAdminPolicy = true;

        return policyAdminResponse;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'policyPremiumBreakdownApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Admin policy'
        );

        const errorStatus = PolicyAdminClient.handlePolicyAdminStatusError(error);

        return errorStatus;
      });
  }

  @serverOnly()
  private static async policyAdminApiBatchGet(
    policyNumbers: Array<string>,
    channel: string
  ): Promise<Array<PolicyAdminPolicy> | undefined> {
    const options = PolicyAdminClient.generateBatchRequestOptions(
      policyNumbers,
      channel,
      PolicyAdminClient.END_POINT_PATHS.GET_POLICY_INFO_BATCH
    );

    if (!options) {
      return undefined;
    }

    return requestPromise(options)
      .then((response) => {
        LoggerSingleton.getInstance().info(
          {
            module: 'PolicyAdminClient',
            function: 'policyAdminApiBatchGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              responseDetail: response,
            },
          },
          `Policy Admin policies fetched for policy numbers [${policyNumbers.join(', ')}]`
        );
        return response && response.payload;
      })
      .catch((error) => {
        const err = {
          name: error.name,
          message: error.message,
          stack: error.stack,
          data: {
            responseDetail: PolicyAdminClient.hideJWTTOKEN(error),
          },
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'PolicyAdminClient',
            function: 'policyAdminApiBatchGet',
            category: Category.request,
            error: err,
          },
          `Unable to find Policy Admin policies for policy numbers [${policyNumbers.join(', ')}]`
        );

        return undefined;
      });
  }

  @serverOnly()
  private static async policyPacketApiGet(policy: Policy): Promise<PolicyAdminPacket> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_POLICY_PACKET
    );

    return requestPromise(options)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'policyPacketApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              tlicEffectiveDate: policy.tLICEffectiveDate,
              effectiveDate: policy.effectiveDate,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Policy Packet'
        );
        return null;
      });
  }

  @serverOnly()
  private static async annuityPacketApiGet(policy: Policy): Promise<PolicyAdminPacket> {
    const options = PolicyAdminClient.generateRequestOptions(
      policy,
      PolicyAdminClient.END_POINT_PATHS.GET_ANNUITY_PACKET
    );

    return requestPromise(options)
      .then((response: { payload: Array<{ type: string; data: string; createdTime: string }> }) => {
        LoggerSingleton.getInstance().info(
          {
            module: 'PolicyAdminClient',
            function: 'annuityPacketApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: response.payload.map((doc) => doc.type),
          },
          'Received multiple documents from Policy Admin'
        );
        // PA uses AnnuityPacket type to store application package
        // but get-policy-docs does not accept this type in the request
        // So we have to fetch all documents and extract what the one we want on HIT side
        const annuityPacket = response.payload.find((doc) => doc.type === 'AnnuityPacket');
        if (!annuityPacket) {
          throw new Error('Unable to find Annuity Packet');
        }
        return { payload: [annuityPacket] };
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'annuityPacketApiGet',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.nonPII },
            data: {
              policyNumber: policy.policyNumber,
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find Annuity Packet'
        );
        return null;
      });
  }

  @serverOnly()
  static async getCashValueInfo(policies: Policy[]): Promise<CashValueQuote[]> {
    LoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'getCashValueInfo',
        category: Category.request,
      },
      'getting'
    );
    const options = PolicyAdminClient.generateCashValueRequestOptions(
      policies,
      PolicyAdminClient.END_POINT_PATHS.CASH_VALUE
    );

    return requestPromise(options)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'getCashValueInfo',
            category: Category.request,
            data: {
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to find cash value info'
        );
        return null;
      });
  }

  private static hideJWTTOKEN(obj) {
    const cpApiGatewayJWTToken = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_JWT_TOKEN);
    return safeJsonStringify(obj).replace(new RegExp(cpApiGatewayJWTToken, 'g'), '');
  }
  /**
   * @param policy the policy to change
   * @param policyAdminOwnerId from owner._id column in PA db, indicating which owner to change
   * @param address the address to be changed to
   * @param principalUserProducerId represents the agent account center user who updates the owner address
   * @param principalUserName represents name of the agent account center user who updates the owner address
   */
  static async updateOwnerAddress(
    policy: Policy,
    policyAdminOwnerId: string,
    isOwnerSameAsInsured: boolean,
    address: Address,
    principalUserProducerId: string,
    principalUserName: string
  ): Promise<SphinxReturnTypes> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'updateOwnerAddress',
        category: UICategory.request,
      },
      'getting'
    );

    const options = PolicyAdminClient.generateUpdateOwnerAddressRequestOptions(
      policy,
      policyAdminOwnerId,
      isOwnerSameAsInsured,
      address,
      principalUserProducerId,
      principalUserName
    );

    UILoggerSingleton.getInstance().info({
      module: 'PolicyAdminClient',
      function: 'updateOwnerAddress',
      category: UICategory.request,
      context: { piiLevel: UIPiiLevelEnum.s2 },
      data: {
        updateOwnerAddressOptions: PolicyAdminClient.hideJWTTOKEN(options),
      },
    });

    return requestPromise(options)
      .then((response) => {
        UILoggerSingleton.getInstance().info(
          {
            module: 'PolicyAdminClient',
            function: 'updateOwnerAddress',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: {
              updateOwnerAddressResponse: response,
            },
          },
          'Updated owner address'
        );
        return PolicyAdminClient.getSphinxReturnType(response);
      })
      .catch((err) => {
        const error = {
          isHumanRelated: false,
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        };
        UILoggerSingleton.getInstance().error(
          {
            module: 'PolicyAdminClient',
            function: 'updateOwnerAddress',
            category: UICategory.request,
            error: error,
          },
          'Unable to update owner address'
        );
        return SphinxReturnTypes.ERROR;
      });
  }

  /***
   * @param policy the policy to change
   * @param paymentFrequency either Annual or Monthly
   * @param principalUserProducerId represents id of the agent account center user who updates the owner address
   * @param principalUserName represents name of the agent account center user who updates the owner address
   */
  static async updatePaymentFrequency(
    policy: Policy,
    paymentFrequency: ProductApiTypes.PaymentFrequency,
    principalUserProducerId: string,
    principalUserName: string
  ): Promise<SphinxReturnTypes> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'updatePaymentFrequency',
        category: UICategory.request,
      },
      'getting'
    );
    const options = PolicyAdminClient.generateupdatePaymentFrequencyRequestOptions(
      policy,
      paymentFrequency,
      principalUserProducerId,
      principalUserName
    );

    UILoggerSingleton.getInstance().info({
      module: 'PolicyAdminClient',
      function: 'updatePaymentFrequency',
      context: {
        piiLevel: UIPiiLevelEnum.s2,
      },
      category: UICategory.request,
      data: {
        updatePaymentFrequencyOptions: PolicyAdminClient.hideJWTTOKEN(options),
      },
    });

    return requestPromise(options)
      .then((response) => {
        return PolicyAdminClient.getSphinxReturnType(response);
      })
      .catch((error) => {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminClient',
            function: 'updatePaymentFrequency',
            category: UICategory.request,
            context: {
              piiLevel: UIPiiLevelEnum.s2,
            },
            data: {
              errorMessage: PolicyAdminClient.hideJWTTOKEN(error),
            },
          },
          'Unable to change payment frequency'
        );
        return SphinxReturnTypes.ERROR;
      });
  }
  protected static generateAnnualStatementRequestOptions(policy: Policy, path: string) {
    const channel: string = policy.getChannelCode();
    const url = PolicyAdminClient.assemblePolicyAdminUrl(channel, path);

    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          referenceId: policy.policyNumber,
          referenceType: 'policyNumber',
          eventName: 'get-policy',
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: {
          policyNumber: policy.policyNumber,
          documentTypes: ['AnnualStatement'],
        },
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }

  protected static generateRequestOptions(policy: Policy, path: string) {
    const channel: string = policy.getChannelCode();
    const url = PolicyAdminClient.assemblePolicyAdminUrl(channel, path);

    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.POST, // Currently all policy-admin endpoints are posts
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          referenceId: policy.policyNumber,
          referenceType: 'policyNumber',
          eventName: path,
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: {
          policyNumber: policy.policyNumber,
        },
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }

  protected static generateBatchRequestOptions(policyNumbers: Array<string>, channel: string, path: string): object {
    if (policyNumbers.length === 0) {
      return undefined;
    }

    const url = PolicyAdminClient.assemblePolicyAdminUrl(channel, path);

    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: {
          policyNumbers: policyNumbers,
        },
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }

  protected static generateHealthcheckRequestOptions(channel: string) {
    const domain = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_URL);
    const path = amorphicStatic.config.nconf.get(PolicyAdminClient.CONFIG_POLICY_ADMIN_HEALTHCHECK_PATH);
    const url = `${domain}${path}`;

    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.GET,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          eventName: path,
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: {},
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }

  protected static generateCashValueRequestOptions(policies: Policy[], path: string) {
    const channel: string = policies[0].getChannelCode();
    const url = PolicyAdminClient.assemblePolicyAdminUrl(channel, path);
    const payload = policies.map((policy) => {
      return {
        policyNumber: policy.policyNumber,
        asOfDate: Utils.now(),
      };
    });
    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          eventName: path,
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: payload,
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }
  private static generateUpdateOwnerAddressRequestOptions(
    policy: Policy,
    policyAdminOwnerId: string,
    isOwnerSameAsInsured: boolean,
    address: Address,
    principalUserProducerId: string,
    principalUserName: string
  ) {
    const path = PolicyAdminClient.SPHINX_END_POINT_PATHS.UPDATE_OWNER_ADDRESS;
    const url = PolicyAdminClient.assembleSphinxUrl(policy.getChannelCode(), path);
    let addressToUpdate = {
      street: address.street,
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: address.state,
      country: address.country,
      zip: address.zip,
    };
    // Get rid of empty properties in the object
    addressToUpdate = JSON.parse(JSON.stringify(addressToUpdate));

    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.PUT,
      json: true,
      body: {
        policyNumber: policy.policyNumber,
        ownerId: policyAdminOwnerId,
        updateInsured: isOwnerSameAsInsured,
        address: addressToUpdate,
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getSphinxPolicyAdminApiHeader(principalUserProducerId, principalUserName),
    };
  }

  private static generateupdatePaymentFrequencyRequestOptions(
    policy: Policy,
    paymentFrequency: ProductApiTypes.PaymentFrequency,
    principalUserProducerId: string,
    principalUserName: string
  ) {
    const path = PolicyAdminClient.SPHINX_END_POINT_PATHS.UPDATE_PAYMENT_FREQUENCY;
    const url = PolicyAdminClient.assembleSphinxUrl(policy.getChannelCode(), path);
    return {
      uri: url,
      method: PolicyAdminClient.REQUEST_TYPES.PUT,
      json: true,
      body: {
        policyNumber: policy.policyNumber,
        frequency: paymentFrequency,
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getSphinxPolicyAdminApiHeader(principalUserProducerId, principalUserName),
    };
  }

  private static handlePolicyAdminStatusError(responseMessage: string) {
    const policyAdminStatusError = new PolicyAdminPolicy();
    policyAdminStatusError.responseDetail = responseMessage;
    policyAdminStatusError.responseStatus = PolicyAdminStatus.POLICY_ADMIN_RESPONSE_STATUSES.QUEUED;
    policyAdminStatusError.isPolicyAdminPolicy = true;

    return policyAdminStatusError;
  }

  private static buildContactInfoRequest(policyNumber: string, ownerInsuredSame: boolean) {
    let contactInfoQuery: string = `query contact_info($request: PolicyInfoRequest) {
                ContactInfo(request: $request) {
                    policy {
                      policyNumber policyId
                      insured {
                          insuredId
                          party {
                              firstName lastName
                          }
                          contactInfo { residentialAddress { street line1 line2 city state zip } phone {number}}
                      }
                      owner {
                        ownerId
                        party {
                            firstName lastName
                        }
                        contactInfo { residentialAddress { street line1 line2 city state zip } phone {number}}
                    }
                    }
        }
    }`;
    if (ownerInsuredSame) {
      contactInfoQuery = `query contact_info($request: PolicyInfoRequest) {
            ContactInfo(request: $request) {
                policy {
                  policyNumber policyId
                  owner {
                    ownerId
                    party {
                        firstName lastName
                    }
                    contactInfo { residentialAddress { street line1 line2 city state zip } phone {number}}
                }
                insured { insuredId }
                }
            }
        }`;
    }

    const contactInfoRequest = {
      query: contactInfoQuery,
      variables: {
        request: {
          context: {
            requestId: '123',
            userId: '123',
            ...PolicyAdminClient.getGlobalHeadersForContext(),
          },
          policyNumber: policyNumber,
        },
      },
    };

    return contactInfoRequest;
  }

  private static buildContactInfoResponse(response): ContactInfoResponse {
    const policy = response.data.ContactInfo.policy;
    const contactInfoResponse: ContactInfoResponse = {
      policy: {
        policyNumber: policy.policyNumber,
        policyId: policy.policyId,
        owner: {
          ownerId: policy.owner.ownerId,
          party: {
            firstName: policy.owner.party.firstName,
            lastName: policy.owner.party.lastName,
          },
          contactInfo: {
            residentialAddress: {
              street: policy.owner.contactInfo.residentialAddress.street,
              line1: policy.owner.contactInfo.residentialAddress.line1,
              line2: policy.owner.contactInfo.residentialAddress.line2,
              city: policy.owner.contactInfo.residentialAddress.city,
              state: policy.owner.contactInfo.residentialAddress.state,
              zip: policy.owner.contactInfo.residentialAddress.zip,
            },
            phone: {
              number: policy.owner.contactInfo.phone.number,
            },
          },
        },
        insured: {
          insuredId: policy.insured.insuredId,
        },
      },
    };
    if (policy.insured.party) {
      contactInfoResponse.policy.insured = {
        insuredId: policy.insured.insuredId,
        party: {
          firstName: policy.insured.party.firstName,
          lastName: policy.insured.party.lastName,
        },
        contactInfo: {
          residentialAddress: {
            street: policy.insured.contactInfo.residentialAddress.street,
            line1: policy.insured.contactInfo.residentialAddress.line1,
            line2: policy.owner.contactInfo.residentialAddress.line2,
            city: policy.insured.contactInfo.residentialAddress.city,
            state: policy.insured.contactInfo.residentialAddress.state,
            zip: policy.insured.contactInfo.residentialAddress.zip,
          },
          phone: {
            number: policy.insured.contactInfo.phone.number,
          },
        },
      };
    }

    return contactInfoResponse;
  }

  private static buildPaymentInfoRequest(policyNumber: string) {
    const paymentInfoQuery: string = `query payment_info($request: PolicyInfoRequest) {
                BankWidget(request: $request) {
                    policy {
                      policyNumber policyId
                      product { productKey { productCode } }
                      payer { payerPartyId payerName paymentType }
                      billing { frequency nextPaymentDate }
                        amount
                    }
                }
            }`;

    const paymentInfoRequest = {
      query: paymentInfoQuery,
      variables: {
        request: {
          context: {
            requestId: '123',
            userId: '123',
            ...PolicyAdminClient.getGlobalHeadersForContext(),
          },
          policyNumber: policyNumber,
        },
      },
    };

    return paymentInfoRequest;
  }

  private static buildPaymentInfoResponse(response): PaymentInfoResponse {
    const policy = response.data.BankWidget.policy;

    const paymentInfoResponse: PaymentInfoResponse = {
      policy: {
        policyNumber: policy.policyNumber,
        policyId: policy.policyId,
        productCode: policy.product.productKey.productCode,
        payer: {
          payerPartyId: policy.payer.payerPartyId,
          payerName: policy.payer.payerName,
          paymentType: policy.payer.paymentType,
        },
        billing: {
          frequency: policy.billing.frequency,
          nextPaymentDate: policy.billing.nextPaymentDate,
        },
        amount: policy.amount,
      },
    };

    return paymentInfoResponse;
  }

  private static buildBeneficiaryInfoRequest(policyNumber: string, channel: string) {
    const compQuery = GglQuery.queryHeader.concat(
      '{',
      GglQuery.policy,
      GglQuery.primaryBeneficiary,
      GglQuery.contingentBeneficiary,
      GglQuery.tertiaryBeneficiary,
      GglQuery.custodian,
      '} }'
    );

    const beneInfoRequest = {
      query: compQuery,
      variables: {
        request: {
          context: {
            requestId: 'beneWidget',
            userId: channel,
            ...PolicyAdminClient.getGlobalHeadersForContext(),
          },
          policyNumber: policyNumber,
        },
      },
    };

    return beneInfoRequest;
  }

  public static buildUpdateOwnerEmailRequest(email: string, policy: Policy, ownerId: string): UpdateOwnerEmailPayload {
    return {
      context: {
        requestId: uuid.v4(),
        userId: 'SanderlingPolicyRepairController',
        ...PolicyAdminClient.getGlobalHeadersForContext(),
      },
      payload: {
        policyNumber: policy.policyNumber,
        email: email,
        ownerId: ownerId,
      },
    };
  }

  public static buildGetOwnerInformationRequest(policy: Policy): GetOwnerInformationPayload {
    return {
      context: {
        requestId: uuid.v4(),
        userId: 'SanderlingPolicyRepairController',
        ...PolicyAdminClient.getGlobalHeadersForContext(),
      },
      payload: {
        policyNumber: policy.policyNumber,
      },
    };
  }

  private static buildInitialInvoiceInfoRequest(
    policy: Policy,
    request: PremiumRequest,
    productPremiumVersion?: string
  ) {
    if (!request) {
      let finalRateClass = policy.finalRateClass || policy.tLICRateClass;
      //TODO cross check if secondary policy get rate class set or not
      if (policy.application.isJointAppWithPendingAmendment() && finalRateClass === null && policy.isSecondary) {
        finalRateClass =
          policy.application.primaryPolicy.finalRateClass || policy.application.primaryPolicy.tLICRateClass;
      }

      request = {
        dob: policy.insured.person.dob,
        product: policy.productWithPendingAmendment(),
        productType: policy.productTypeWithPendingAmendment(),
        effectiveDate: policy.effectiveDateWithPendingAmendment(),
        asOfDate: moment(Utils.now()).startOf('day').toDate(),
        gender: policy.insured.person.gender,
        ratingCode: finalRateClass,
        state:
          policy.application.isJointApp() || !policy.getOwnerResidentialAddress().state
            ? policy.application.primaryPolicy.getOwnerResidentialAddress().state
            : policy.getOwnerResidentialAddress().state,
        term: policy.termWithPendingAmendment(),
        face: policy.faceAmountWithPendingAmendment(),
        flatExtras: policy.premiumBreakdown
          ? policy.premiumBreakdown.getAllAggregateFlatExtrasWithPendingAmendment()
          : [],
        paymentFrequency: policy.paymentFrequencyWithPendingAmendment(),
        waiver: policy.hasWaiverWithPendingAmendment(),
        riders: policy.ridersWithPendingAmendment(),
      };
    }

    //Initial invoice endpoint expects the same request like premium service(product) call
    const InitialInvoiceRequest = ProductServiceClient.buildPremiumRequest(
      policy ? policy.policyNumber : '',
      policy ? policy.productPremiumVersion : productPremiumVersion,
      request
    );
    const initialInvoiceInfoRequest = {
      query: GglQuery.initialInvoiceQueryHeader,
      variables: {
        request: InitialInvoiceRequest.variables.request,
      },
    };

    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'buildInitialInvoiceInfoRequest',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          productServicePayload: safeJsonStringify(initialInvoiceInfoRequest),
        },
      },
      'Fetching initial invoice in a backdated scenario from PA endpoint'
    );

    return initialInvoiceInfoRequest;
  }

  private static buildBeneficiaryInfoResponse(response): BeneficiaryInfoResponse {
    const BeneInfo = response.data.PolicyBeneficiaryInfo;

    const BeneInfoResponse: BeneficiaryInfoResponse = {
      policy: {
        policyNumber: BeneInfo.policyNumber,
        policyId: BeneInfo.policyId,
        primaryBeneficiary: BeneInfo.primaryBeneficiary,
        contingentBeneficiary: BeneInfo.contingentBeneficiary,
        tertiaryBeneficiary: BeneInfo.tertiaryBeneficiary,
        custodian: BeneInfo.custodian,
        ownerResidentialState: BeneInfo.ownerResidentialState,
      },
    };

    return BeneInfoResponse;
  }

  private static buildInitialInvoiceInfoResponse(response): InitialInvoice {
    const initialInvoice: InitialInvoice = response.data.initialInvoice;
    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'buildInitialInvoiceInfoResponse',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          productServicePayload: safeJsonStringify(initialInvoice),
        },
      },
      'Response received from PA endpoint for Initial Invoice'
    );
    return initialInvoice;
  }

  private static buildCanCreateTransactionsRequest(
    policy: Policy,
    policyAdminPolicyId: string,
    workflowNames: string[]
  ) {
    const channel: string = policy.getChannelCode();
    return {
      uri: PolicyAdminClient.assemblePolicyAdminUrl(
        channel,
        PolicyAdminClient.END_POINT_PATHS.CAN_CREATE_TRANSACTION_WORKFLOW
      ),
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: {
        context: {
          requestId: Utils.generateUuid(),
          userId: channel,
          eventName: 'can-create-transaction-workflow',
          referenceId: policyAdminPolicyId,
          referenceType: 'PolicyId',
          ...PolicyAdminClient.getGlobalHeadersForContext(),
        },
        payload: {
          policyId: policyAdminPolicyId,
          workflowNames: workflowNames,
          policyWorkflowName: 'Policy',
        },
      },
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };
  }

  private static getSphinxReturnType(response): SphinxReturnTypes {
    if (response.statusCode && response.statusCode === 200) {
      return SphinxReturnTypes.SUCCESS;
    }
    return SphinxReturnTypes.ERROR;
  }
}

export class PolicyAdminPolicy {
  policyInfo: PolicyInfo;
  supportInfo: SupportInfo;
  responseStatus: string;
  responseDetail: string;

  isPolicyAdminPolicy: boolean;
  policyHoldStatus: string;
  hasThirdPartyPayer: boolean;
  hasNoPayer: boolean;
  ownerIsEntity: boolean;
  rateClass: string;
  waiverOfPremium: boolean;
  paymentType: string;
  paymentFrequency: string;
  acceleratedBenefitRider: boolean;

  // @TODO remove this once cp starts using party data from eventor
  owners: Array<TPolicyAdminPartyInfo>;
  insured: TPolicyAdminPartyInfo;
}

export interface PolicyAdminPartialConversionQuote {
  payload: {
    premiumBefore: number;
    premiumAfter: number;
    breakdownBefore: Segment | Fee | LifeFlatExtra[];
    breakdownAfter: Segment | Fee | LifeFlatExtra[];
    cancelPolicy: boolean;
    breakdownCloseout: Segment | Fee | LifeFlatExtra[];
    total: number;
  };
}

export interface Segment {
  type: string;
  benefitKey?: {
    benefitCode: string;
    productType: string;
    productCode: string;
    state: string;
  };
  description: string;
  amount: number;
}
export interface Fee {
  type: string;
  amount: number;
}
export interface LifeFlatExtra {
  type: string;
  description: string;
  amount: number;
}

export interface PolicyInfo {
  status: string;
  statusCode: string;
  issuedDate: string;
  nextPaymentDate: string;
  tlicEffectiveDate: string;
  effectiveDate: string;
  terminationDate: string;
  freeLookEndDate: string;
  graceStartDate: string;
  graceEndDate: string;
  lapsedDate: string;
  policyAdminPolicyId: string;
  policyNumber: string;
  conversionEndDate: string;
  conversionCreditPercent?: number;
  conversionCreditExpirationDate?: string;
  faceAmount: number;
  lastUpdatedTimeOnParty: string;
  paidToDate: string;
  basePremiumAmount: number;
  waiverPremiumAmount: number;
  substandardPremiumAmount: number;
  feeAmount: number;
  flatExtraAmount: number;
  ownerName: string;
  duration: number;
}

export interface SupportInfo {
  supportEmail: string;
  supportLine: string;
}

export interface PolicyAdminPacket {
  payload: Array<PolicyAdminDoc>;
}

export interface PolicyAdminDoc {
  name: string;
  mimeType: string;
  data: string;
  createdTime: string;
  type: string;
}

export interface PaymentInfoResponse {
  policy: {
    policyNumber: string | null;
    policyId: string | null;
    productCode: string | null;
    payer: {
      payerPartyId: string | null;
      payerName: string | null;
      paymentType: string | null;
    };
    billing: {
      frequency: string | null;
      nextPaymentDate: string | null;
    };
    amount: number | null;
  };
}

export interface ContactInfoResponse {
  policy: {
    policyNumber: string | null;
    policyId: string | null;
    insured: {
      insuredId: string | null;
      party?: {
        firstName: string | null;
        lastName: string | null;
      };
      contactInfo?: {
        residentialAddress: {
          street: string | null;
          line1: string | null;
          line2: string | null;
          state: string | null;
          city: string | null;
          zip: string | null;
        };
        phone: {
          number: string | null;
        };
      };
    };
    owner: {
      ownerId: string | null;
      party: {
        firstName: string | null;
        lastName: string | null;
      };
      contactInfo: {
        residentialAddress: {
          street: string | null;
          line1: string | null;
          line2: string | null;
          state: string | null;
          city: string | null;
          zip: string | null;
        };
        phone: {
          number: string | null;
        };
      };
    };
  };
}

export interface FlatExtraJson {
  annualPremium: number;
  modelRate: number;
  name: string;
  premium: number;
  rate: number;
}

export interface PolicySegmentBenefit {
  basePremium: number;
  code: string;
  fee: number;
  flatExtras: Array<FlatExtraJson>;
  rate: number;
  tableRate: number;
  tableRatingPremium: number;
  baseWaiverAmount: number;
}

export interface PolicySegment {
  id: string;
  premium: number;
  rating: { [key: string]: number };
  riderType: string;
  startDate: Date;
  benefits: Array<PolicySegmentBenefit>;
}

export interface PolicyPremiumBreakdown {
  annualizedPremium: number;
  paymentFrequency: string;
  premium: number;
  segments: Array<PolicySegment>;
  responseStatus: string;
  isPolicyAdminPolicy: boolean;
}

export interface CanCreateTransactionsResponse {
  canCreateWorkflows: { [k: string]: boolean };
}

export type PolicyAdminRequestPartialConversionQuotePayload = {
  policyNumber: string;
  effectiveDate: DateDMY;
  conversionAmount: number;
  quote: boolean;
};

export interface CashValueQuote {
  policyNumber: string | null;
  asOfDate: string | null;
  guaranteedCashValue: number | null;
  PUAGuaranteedCashValue: number | null;
  totalAccumulatedDividendValue: number | null;
  unearnedPremium: number | null;
  totalOutstandingLoanAmount: number | null;
  grossCashValue: number | null;
  netCashValue: number | null;
}

export interface UpdateOwnerEmailPayload {
  context: {
    requestId: string;
    userId: string;
    globalId: string;
    globalSource: string;
  };
  payload: {
    policyNumber: string;
    email: string;
    ownerId: string;
  };
}

export interface GetOwnerInformationPayload {
  context: {
    requestId: string;
    userId: string;
    globalId: string;
    globalSource: string;
  };
  payload: {
    policyNumber: string;
  };
}

export type Person = {
  person?: {
    govtId: string;
    govtIdType: string;
  };
};

export type Entity = {
  entity?: {
    govtId: string;
    govtIdType: string;
  };
};

export type PersonOrEntity = Person & Entity;

export interface OwnerInformation {
  id: string;
  party: PersonOrEntity;
  contactInfo: {
    email: string;
  };
}

export type GetOwnerInformationSuccess = Array<OwnerInformation>;

export enum PAGovtIdTypes {
  ssn = 'ssn',
  ein = 'ein',
  itin = 'itin',
}
